import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

const AsignacionModulos = () => {
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [rolSeleccionado, setRolSeleccionado] = useState(null);
  const [permisos, setPermisos] = useState({});
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingModulos, setLoadingModulos] = useState(false);
  const [loadingPermisos, setLoadingPermisos] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const crearEstadoInicial = (listaModulos = []) => {
    const estado = {};

    listaModulos.forEach((modulo) => {
      const moduloId = modulo._id;
      estado[moduloId] = {
        crear: false,
        leer: false,
        actualizar: false,
        eliminar: false,
      };
    });

    return estado;
  };

  const obtenerPermisosPorRol = async (rolId, listaModulosActual = modulos) => {
    try {
      if (!rolId || !listaModulosActual.length) {
        setPermisos(crearEstadoInicial(listaModulosActual));
        return;
      }

      setLoadingPermisos(true);

      const { data } = await axios.get(`/api/seguridad/rolmodulo/rol/${rolId}`);

      if (data?.ok) {
        const modulosConPermisos = Array.isArray(data.modulos) ? data.modulos : [];
        const nuevoEstado = crearEstadoInicial(listaModulosActual);

        modulosConPermisos.forEach((modulo) => {
          nuevoEstado[modulo._id] = {
            crear: modulo.puedeCrear || false,
            leer: modulo.puedeLeer || false,
            actualizar: modulo.puedeActualizar || false,
            eliminar: modulo.puedeEliminar || false,
          };
        });

        setPermisos(nuevoEstado);
      } else {
        setPermisos(crearEstadoInicial(listaModulosActual));
      }
    } catch (error) {
      console.error("Error al obtener permisos del rol:", error);
      setPermisos(crearEstadoInicial(listaModulosActual));

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "No se pudieron cargar los permisos del rol",
      });
    } finally {
      setLoadingPermisos(false);
    }
  };

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoadingRoles(true);
        setLoadingModulos(true);

        const [rolesRes, modulosRes] = await Promise.all([
          axios.get("/api/seguridad/roles/list"),
          axios.get("/api/seguridad/modulos/list"),
        ]);

        const listaRoles =
          rolesRes.data?.ok && Array.isArray(rolesRes.data.roles)
            ? rolesRes.data.roles
            : [];

        const listaModulos =
          modulosRes.data?.ok && Array.isArray(modulosRes.data.modulos)
            ? modulosRes.data.modulos
            : [];

        setRoles(listaRoles);
        setModulos(listaModulos);
        setPermisos(crearEstadoInicial(listaModulos));

        if (listaRoles.length > 0) {
          setRolSeleccionado(listaRoles[0]);
          await obtenerPermisosPorRol(listaRoles[0]._id, listaModulos);
        }
      } catch (error) {
        console.error("Error al cargar datos iniciales:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudieron cargar los roles y módulos",
        });
      } finally {
        setLoadingRoles(false);
        setLoadingModulos(false);
      }
    };

    cargarDatos();
  }, []);

  const seleccionarRol = async (rol) => {
    setRolSeleccionado(rol);
    await obtenerPermisosPorRol(rol._id, modulos);
  };

  const editarRol = (e, rol) => {
    e.stopPropagation();
    navigate(`/seguridad/roles/editar/${rol._id}`, {
      state: { rol },
    });
  };

  const editarModulo = (e, modulo) => {
    e.stopPropagation();
    navigate(`/seguridad/modulos/editar/${modulo._id}`, {
      state: { modulo },
    });
  };

  const handleCheck = (moduloId, permiso) => {
    setPermisos((prev) => ({
      ...prev,
      [moduloId]: {
        ...prev[moduloId],
        [permiso]: !prev[moduloId]?.[permiso],
      },
    }));
  };

  const limpiarTodo = () => {
    setPermisos(crearEstadoInicial(modulos));
  };

  const marcarTodo = () => {
    const nuevoEstado = {};

    modulos.forEach((modulo) => {
      nuevoEstado[modulo._id] = {
        crear: true,
        leer: true,
        actualizar: true,
        eliminar: true,
      };
    });

    setPermisos(nuevoEstado);
  };

  const guardarAsignacion = async () => {
    try {
      if (!rolSeleccionado?._id) {
        return Swal.fire({
          icon: "warning",
          title: "Rol requerido",
          text: "Debes seleccionar un rol",
        });
      }

      if (!modulos.length) {
        return Swal.fire({
          icon: "warning",
          title: "Sin módulos",
          text: "No hay módulos para asignar permisos",
        });
      }

      setGuardando(true);

      const payload = {
        rolObjectId: rolSeleccionado._id,
        modulos: modulos.map((modulo) => ({
          moduloObjectId: modulo._id,
          puedeCrear: permisos[modulo._id]?.crear || false,
          puedeLeer: permisos[modulo._id]?.leer || false,
          puedeActualizar: permisos[modulo._id]?.actualizar || false,
          puedeEliminar: permisos[modulo._id]?.eliminar || false,
        })),
      };

      const { data } = await axios.post(
        "/api/seguridad/rolmodulo/save-permisos",
        payload
      );

      Swal.fire({
        icon: "success",
        title: data?.message || "Permisos guardados correctamente",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Error al guardar asignación:", error);
      Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text: error.response?.data?.message || "No se pudieron guardar los permisos",
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mb-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h4 className="mb-1 fw-semibold">Asignación de Módulos y Permisos</h4>
              <p className="mb-0 text-secondary small">
                Selecciona un rol y configura sus permisos sobre los módulos del sistema.
              </p>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={marcarTodo}
                disabled={!modulos.length || loadingPermisos}
              >
                <i className="fa-solid fa-check-double me-2"></i>
                Marcar todo
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={limpiarTodo}
                disabled={!modulos.length || loadingPermisos}
              >
                <i className="fa-solid fa-eraser me-2"></i>
                Limpiar
              </button>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {/* Panel de roles */}
          <div className="col-12 col-lg-4">
            <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 h-100">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h6 className="mb-0 fw-semibold">Roles del sistema</h6>
                  <small className="text-secondary">Selecciona un rol para asignar permisos</small>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <span className="badge text-bg-light border text-secondary rounded-pill">
                    {roles.length} roles
                  </span>

                  <button
                    type="button"
                    className="btn btn-dark btn-sm"
                    onClick={() => navigate("/seguridad/roles/registrar")}
                  >
                    <i className="fa-solid fa-plus me-1"></i>
                    Nuevo rol
                  </button>
                </div>
              </div>

              <hr className="my-3" />

              {loadingRoles ? (
                <div className="text-center py-4 text-secondary">Cargando roles...</div>
              ) : roles.length > 0 ? (
                <div className="d-flex flex-column gap-2">
                  {roles.map((rol) => {
                    const activo = rolSeleccionado?._id === rol._id;

                    return (
                      <div
                        key={rol._id}
                        role="button"
                        onClick={() => seleccionarRol(rol)}
                        className={`border rounded-4 px-3 py-3 ${
                          activo ? "bg-dark text-white" : "bg-light"
                        }`}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="fw-semibold">{rol.nombreRol}</span>

                              {activo ? (
                                <span className="badge bg-white text-dark rounded-pill">
                                  Activo
                                </span>
                              ) : (
                                <i className="fa-solid fa-angle-right text-secondary"></i>
                              )}
                            </div>

                            {rol.descripcionRol ? (
                              <div
                                className={`small mt-1 ${
                                  activo ? "text-light" : "text-secondary"
                                }`}
                              >
                                {rol.descripcionRol}
                              </div>
                            ) : null}
                          </div>

                          <button
                            type="button"
                            className={`btn btn-sm ${
                              activo ? "btn-light" : "btn-outline-primary"
                            }`}
                            title="Editar rol"
                            onClick={(e) => editarRol(e, rol)}
                          >
                            <i className="fa-solid fa-pen-to-square"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4 text-secondary">
                  No hay roles registrados.
                </div>
              )}
            </div>
          </div>

          {/* Panel de módulos y permisos */}
          <div className="col-12 col-lg-8">
            <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h6 className="mb-0 fw-semibold">Módulos y permisos</h6>
                  <small className="text-secondary">
                    Rol actual:{" "}
                    <span className="fw-semibold">
                      {rolSeleccionado?.nombreRol || "Sin seleccionar"}
                    </span>
                  </small>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="badge text-bg-light border text-secondary rounded-pill">
                    {modulos.length} módulos
                  </span>

                  <button
                    type="button"
                    className="btn btn-outline-dark btn-sm"
                    onClick={() => navigate("/seguridad/modulos/registrar")}
                  >
                    <i className="fa-solid fa-plus me-1"></i>
                    Nuevo módulo
                  </button>
                </div>
              </div>

              <hr className="my-3" />

              {loadingModulos || loadingPermisos ? (
                <div className="text-center py-4 text-secondary">
                  Cargando módulos y permisos...
                </div>
              ) : modulos.length > 0 ? (
                <>
                  <div className="d-none d-md-block">
                    <div className="row g-2 px-2 mb-2">
                      <div className="col-md-4">
                        <small className="text-secondary text-uppercase fw-semibold">
                          Módulo
                        </small>
                      </div>
                      <div className="col-md-2 text-center">
                        <small className="text-secondary text-uppercase fw-semibold">
                          Crear
                        </small>
                      </div>
                      <div className="col-md-2 text-center">
                        <small className="text-secondary text-uppercase fw-semibold">
                          Leer
                        </small>
                      </div>
                      <div className="col-md-2 text-center">
                        <small className="text-secondary text-uppercase fw-semibold">
                          Actualizar
                        </small>
                      </div>
                      <div className="col-md-2 text-center">
                        <small className="text-secondary text-uppercase fw-semibold">
                          Eliminar
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-3">
                    {modulos.map((modulo) => (
                      <div key={modulo._id} className="border rounded-4 p-3">
                        <div className="row align-items-center g-3">
                          <div className="col-12 col-md-4">
                            <div className="d-flex justify-content-between align-items-start gap-2">
                              <div className="flex-grow-1">
                                <div className="fw-semibold">{modulo.nombreModulo}</div>
                                <div className="small text-secondary d-md-none">
                                  Configura permisos para este módulo
                                </div>
                                {modulo.descripcionModulo ? (
                                  <div className="small text-secondary mt-1">
                                    {modulo.descripcionModulo}
                                  </div>
                                ) : null}
                              </div>

                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                title="Editar módulo"
                                onClick={(e) => editarModulo(e, modulo)}
                              >
                                <i className="fa-solid fa-pen-to-square"></i>
                              </button>
                            </div>
                          </div>

                          <div className="col-6 col-md-2 text-md-center">
                            <label className="form-label small text-secondary text-uppercase d-md-none">
                              Crear
                            </label>
                            <div>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={permisos[modulo._id]?.crear || false}
                                onChange={() => handleCheck(modulo._id, "crear")}
                              />
                            </div>
                          </div>

                          <div className="col-6 col-md-2 text-md-center">
                            <label className="form-label small text-secondary text-uppercase d-md-none">
                              Leer
                            </label>
                            <div>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={permisos[modulo._id]?.leer || false}
                                onChange={() => handleCheck(modulo._id, "leer")}
                              />
                            </div>
                          </div>

                          <div className="col-6 col-md-2 text-md-center">
                            <label className="form-label small text-secondary text-uppercase d-md-none">
                              Actualizar
                            </label>
                            <div>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={permisos[modulo._id]?.actualizar || false}
                                onChange={() => handleCheck(modulo._id, "actualizar")}
                              />
                            </div>
                          </div>

                          <div className="col-6 col-md-2 text-md-center">
                            <label className="form-label small text-secondary text-uppercase d-md-none">
                              Eliminar
                            </label>
                            <div>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={permisos[modulo._id]?.eliminar || false}
                                onChange={() => handleCheck(modulo._id, "eliminar")}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-4 text-secondary">
                  No hay módulos registrados.
                </div>
              )}

              <div className="row g-3 mt-3">
                <div className="col-12 col-md-6">
                  <button
                    type="button"
                    className="btn btn-dark w-100 fw-semibold py-2"
                    onClick={guardarAsignacion}
                    disabled={guardando || !rolSeleccionado || !modulos.length}
                  >
                    <i className="fa-solid fa-floppy-disk me-2"></i>
                    {guardando ? "Guardando..." : "Guardar asignación"}
                  </button>
                </div>

                <div className="col-12 col-md-6">
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100 fw-semibold py-2"
                    onClick={limpiarTodo}
                    disabled={!modulos.length || loadingPermisos}
                  >
                    <i className="fa-solid fa-broom me-2"></i>
                    Limpiar permisos
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center text-secondary small">
          Verifica la configuración antes de guardar los permisos del rol seleccionado.
        </div>
      </div>
    </div>
  );
};

export default AsignacionModulos;